import React from 'react';
import { pull, uniq, map } from 'lodash';
import FieldsSelectorHelper from './FieldsSelectorHelper';

class FieldSelector extends React.Component {

    constructor(props) {
        super(props);

        this.state = {
            settings:     props.settings,
            current:      props.current,
            initial:      Object.assign({}, props.current),
            dragOverField: null,
        };

        // Identity of what's being dragged — instance var to avoid
        // setState on every dragover tick.
        this.draggingField = null;

        this.changeFields = this.changeFields.bind(this);
        this.applyFilter  = this.applyFilter.bind(this);
        this.resetFilter  = this.resetFilter.bind(this);
    }

    // ─── Original logic (unchanged) ─────────────────────────────────────────

    changeFields(e) {
        let { name, value } = e.target,
            currentFields = this.state.current.columns.slice(0),
            allFields     = this.state.settings,
            settingFields = this.props.settings;

        const isCatalog  = e.target.closest('.catalog-field-selector');
        const totalField = settingFields.find(field => field.id === 'overview');
        let fieldsToChange = [value];

        for (let field of allFields) {
            if (field.sourceGroup && field.sourceGroup === value) {
                fieldsToChange.push(field.id);
            } else if (
                field.sourceGroup == null &&
                value === 'overview' &&
                !totalField.isTotalExpanded &&
                !field.required &&
                isCatalog
            ) {
                fieldsToChange.push(field.id);
            }
        }

        if (currentFields.includes(value)) {
            for (let f of fieldsToChange) pull(currentFields, f);
        } else {
            for (let f of fieldsToChange) currentFields.push(f);
        }

        this.setState({ current: { ...this.state.current, [name]: currentFields } });
    }

    resetFilter() {
        const allColumns = map(this.state.settings, 'id');
        this.setState({
            current: { ...this.state.initial, columns: allColumns },
        });
    }

    applyFilter() {
        const filter  = this.state.current;
        const columns = filter.columns.filter(id => !this.getGroupHeaderIds().has(id));
        if (!columns.length)
            alert('Please select at least one field');
        else
            this.props.onUpdate({ ...filter, columns });
    }

    // ─── Right-panel drag handlers ───────────────────────────────────────────

    onDragStart(e, fieldId) {
        this.draggingField = fieldId;
        e.dataTransfer.effectAllowed = 'move';
    }

    onDragOver(e, fieldId) {
        e.preventDefault();
        if (this.state.dragOverField !== fieldId) {
            this.setState({ dragOverField: fieldId });
        }
    }

    onDragEnd() {
        this.draggingField = null;
        this.setState({ dragOverField: null });
    }

    onDrop(e, targetId) {
        e.preventDefault();

        const dragging = this.draggingField;
        this.draggingField = null;
        this.setState({ dragOverField: null });

        if (!dragging || dragging === targetId) return;

        const columns = this.state.current.columns.slice(0);
        const fromIdx = columns.indexOf(dragging);
        const toIdx   = columns.indexOf(targetId);
        if (fromIdx === -1 || toIdx === -1) return;

        columns.splice(fromIdx, 1);
        columns.splice(toIdx, 0, dragging);

        this.setState({ current: { ...this.state.current, columns } });
    }

    // ─── Helpers ─────────────────────────────────────────────────────────────

    getFieldLabel(id) {
        const field = this.state.settings.find(f => f.id === id);
        return field ? (field.headerAlt || field.Header || id) : id;
    }

    /**
     * IDs that act as group-select toggles — any field whose id appears as a
     * sourceGroup value on other fields.  Utility-only: hidden from the right
     * panel and stripped from the apply output.
     */
    getGroupHeaderIds() {
        let groupSet = new Set(
            this.state.settings.map(f => f.sourceGroup).filter(Boolean)
        );
        groupSet.add('overview');
        return groupSet;
    }

    /**
     * Ordered list of real (non-required, non-expander, non-group-header) IDs
     * that are currently checked — drives the right panel.
     */
    getSelectedColumns() {
        const groupHeaderIds = this.getGroupHeaderIds();
        const selectableIds  = new Set(
            FieldsSelectorHelper.pickFields(this.state.settings)
                .filter(f => f.expander !== true && f.required !== true && !groupHeaderIds.has(f.id))
                .map(f => f.id)
        );
        return this.state.current.columns.filter(id => selectableIds.has(id));
    }

    // ─── Render ──────────────────────────────────────────────────────────────

    render() {
        const fields         = FieldsSelectorHelper.pickFields(this.state.settings);
        const groups         = uniq(map(this.state.settings, field => field.sourceGroup));
        const selectedCols   = this.getSelectedColumns();
        const { dragOverField } = this.state;
        const extraClassName = this.props.extraClassName || '';

        return (
            <div className={`fields-selector-root ${extraClassName}`}>

                {/* ── Header ── */}
                <div className="manage-fields-header">
                    <div className="links-holder">
                        <a className="reset" onClick={this.resetFilter}>Select All</a>
                        <a className="apply" onClick={this.applyFilter}>Apply</a>
                    </div>
                </div>

                {/* ── Two-column body ── */}
                <div className="manage-fields-body">

                    {/* LEFT — grouped checkboxes, exactly as before */}
                    <div className="manage-fields-selection">
                        {groups.map((group) => {
                            const groupClassName = group == null ? 'main-group' : 'vendor-group';
                            return (
                                <div key={group ?  group : '__main__'} className={`group-container ${groupClassName}`}>
                                    {fields.map((item) => {
                                        if (item.expander  === true) return null;
                                        if (item.required  === true) return null;
                                        if (item.sourceGroup !== group) return null;

                                        return (
                                            <div key={item.id} className="manage-fields-item">
                                                <div className="input-holder">
                                                    <input
                                                        type="checkbox"
                                                        name="columns"
                                                        value={item.id}
                                                        onChange={this.changeFields}
                                                        checked={this.state.current.columns.includes(item.id)}
                                                        id={item.id}
                                                        className="input-checkbox visually-hidden"
                                                    />
                                                    <label className="default-checkbox" htmlFor={item.id}>
                                                        {item.headerAlt || item.Header}
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>

                    {/* RIGHT — flat sortable list of selected columns */}
                    <div className="manage-fields-order">
                        {selectedCols.length === 0 && (
                            <div className="order-empty">No columns selected</div>
                        )}
                        {selectedCols.map((colId) => {
                            const isDragOver = dragOverField === colId && this.draggingField !== colId;
                            return (
                                <div
                                    key={colId}
                                    className={`order-item${isDragOver ? ' drag-over' : ''}`}
                                    draggable
                                    onDragStart={(e) => this.onDragStart(e, colId)}
                                    onDragOver={(e)  => this.onDragOver(e, colId)}
                                    onDragEnd={()    => this.onDragEnd()}
                                    onDrop={(e)      => this.onDrop(e, colId)}
                                >
                                    <span className="drag-handle" aria-hidden="true">⠿</span>
                                    <span className="order-item-label">{this.getFieldLabel(colId)}</span>
                                </div>
                            );
                        })}
                    </div>

                </div>
            </div>
        );
    }
}

export default FieldSelector;